import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  DisplayProps,
  HeightProps,
  JustifyContentProps,
  MaxWidthProps,
  MinWidthProps,
  OverflowProps,
  PositionProps,
  ResponsiveValue,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  color,
  display,
  height,
  justifyContent,
  maxWidth,
  minWidth,
  overflow,
  position,
  space,
  themeGet,
} from 'styled-system';

import { BodyBoldBB2 } from 'common/components/Typography';
import { isTouchDevice } from 'common/DeviceDetector';
import type { Color } from 'common/theme/colors/types';
import type { ColorProps, SpaceProps } from 'common/theme/system-types';
import type {
  BoxSizingProps,
  TextDecorationProps,
  TextOverflowProps,
  WhiteSpaceProps,
} from 'common/theme/system-utilities';
import { boxSizing, textDecoration, textOverflow, whiteSpace } from 'common/theme/system-utilities';
import { DownArrow, downArrowStyles } from './Arrow';

interface CommonTabBarChipProps {
  text: string;
  activeBackgroundColor: string;
  activeTextColor: string;
  link?: Nullable<string>;
  isActive?: boolean;
  ariaLabel?: string;
  onClick?: () => void;
}

type ChipProps = DisplayProps &
  BackgroundColorProps &
  ColorProps &
  TextDecorationProps &
  BorderProps &
  BorderColorProps &
  BorderRadiusProps &
  AlignItemsProps &
  JustifyContentProps &
  SpaceProps &
  HeightProps &
  MaxWidthProps &
  MinWidthProps &
  BoxSizingProps &
  PositionProps;

const Chip = styled.a<ChipProps & { hoverEnabled?: boolean; textColor?: string }>`
  ${display};
  ${backgroundColor};
  ${color};
  ${textDecoration};
  ${border};
  ${borderColor};
  ${borderRadius};
  ${alignItems};
  ${justifyContent}
  ${space};
  ${height};
  ${boxSizing};
  ${maxWidth};
  ${minWidth};
  ${position};

  ${props =>
    props.hoverEnabled &&
    `${
      isTouchDevice()
        ? `
          &:active {
            background-color: ${props.textColor};
            border-color: ${props.backgroundColor as string};
            opacity: 0.9;

            span {
              color: ${props.backgroundColor as string};
            }
          }`
        : `
          &:hover {
            background-color: ${props.textColor};
            border-color: ${props.backgroundColor as string};
            opacity: 0.9;

            span {
              color: ${props.backgroundColor as string};
            }
          }
          &:active {
            opacity: 1;
          }
    `
    }
  `}
  
  &:focus {
    boxSizing: 'border-box',
    outline-offset: 3px;
    outline: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
  }
`;

const chipStyles: ComponentProps<typeof Chip> = {
  position: 'relative',
  display: 'inline-flex',
  textDecoration: 'none',
  border: '1px solid',
  borderRadius: 'x-large',
  alignItems: 'center',
  justifyContent: 'center',
  px: 'sp_16',
  boxSizing: 'border-box',
  height: ['44px', null, null, '48px'],
  minWidth: '56px',
  maxWidth: '200px',
};

const Label = styled(BodyBoldBB2)<WhiteSpaceProps & OverflowProps & TextOverflowProps>`
  ${whiteSpace};
  ${overflow};
  ${textOverflow};
  ${color};
`;

const labelStyles: ComponentProps<typeof Label> = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  as: 'span',
};

export const CommonTabBarChip = ({
  text,
  activeBackgroundColor,
  activeTextColor,
  link,
  isActive,
  ariaLabel,
  onClick,
}: CommonTabBarChipProps) => {
  const textColor = isActive ? activeTextColor : activeBackgroundColor;
  const bgColor = isActive ? activeBackgroundColor : activeTextColor;

  return (
    <Chip
      {...chipStyles}
      backgroundColor={bgColor as ResponsiveValue<Color>}
      borderColor={textColor}
      textColor={textColor}
      hoverEnabled={!isActive}
      href={!isActive ? link : undefined}
      onClick={onClick}
      {...(ariaLabel && { ['aria-label']: ariaLabel })}
    >
      <Label color={textColor as Color} {...labelStyles}>
        {text}
      </Label>
      {isActive && (
        <DownArrow {...downArrowStyles} backgroundColor={bgColor} borderColor={textColor} />
      )}
    </Chip>
  );
};
